import { StoreApi } from 'zustand';

import { TTextLocalized } from '@northladder/utilities';

import { IDateRangeType } from '../../types';

/**
 * Defines the interface for raw filter options on `DealsListScreen`
 */
export interface IDealsScreenFilterOption {
  label: TTextLocalized;
  value: string;
}

export enum ECourierStatus {
  IN_TRANSIT = 'In Transit',
  DELIVERED = 'Delivered',
  RETURNED = 'Returned',
  DELIVERY_REFUSED = 'Delivery Refused',
  CANCELLED = 'Cancelled',
  GRADE_REVIEW = 'Grade Review',
  NOT_AVAILABLE = 'Not Available',
}

/**
 * Defines the interface for formatted response for the appraisal list.
 */
export interface IDealListAppraisal {
  appraisalCode: string;
  assetName: TTextLocalized;
  assetNameLabel: string;
  categoryName?: string;
  courierStatus: ECourierStatus;
  dealDate: number;
  deliveryDate: number;
  grade?: string;
  serialNumber?: string;
  displayStatus: string[];
  imeiNumber: string;
  isDelivered: boolean;
  purchasePrice: number;
  stackID?: string;
}

export type TAccountStatementType =
  | 'STATEMENT_TYPE_BULK_PURCHASE'
  | 'STATEMENT_TYPE_PAYMENT'
  | 'STATEMENT_TYPE_TRANSACTION';

/**
 * Transformed Interface for all the common account statement details for all the Cards
 * (bulk purchase, transactions, payment details) in transaction screen.
 */
export interface IBaseAccountStatement
  extends Omit<IDealListAppraisal, 'appraisalCode'> {
  id: string;
  statementType: TAccountStatementType;
}

/**
 * Transformed Transaction card details
 */
export interface ITransactionAccountStatement extends IBaseAccountStatement {
  statementType: 'STATEMENT_TYPE_TRANSACTION';
  appraisalCode: string;
}

/**
 * Transformed Bulk purchase card details
 */
export interface IBulkPurchaseAccountStatement extends IBaseAccountStatement {
  statementType: 'STATEMENT_TYPE_BULK_PURCHASE';
  stackId: string;
  isSingleTransaction: boolean;
  transactions: ITransactionAccountStatement[];
  invoiceUrl: string;
}

/**
 * Transformed payment card details.
 */
export interface IPaymentAccountStatement extends IBaseAccountStatement {
  narrative: string;
  statementType: 'STATEMENT_TYPE_PAYMENT';
  type: string;
}

export type TAccountStatement =
  | IBulkPurchaseAccountStatement
  | IPaymentAccountStatement
  | ITransactionAccountStatement;

/**
 * Interface for the records returned in `IAccountStatementData`.
 */
export interface IAccountStatementRecord {
  title: string;
  data: TAccountStatement[];
}

export interface ITransactionFilter<TLabel = unknown> {
  label: TLabel;
  value: string;
}

export type TTransactionListType = 'ALL' | 'IN-TRANSIT';

/**
 * This determines the type of transactions records being fetched, whether
 * `All` for all running balance transactions or the ones `In Transit`.
 * This is used in the tabbed switch control.
 */
export interface IActiveTxnListType<T = unknown> {
  id: TTransactionListType;
  label: T;
}

/**
 * These types are used for tracking stateful logic for the Transactions Store
 */
export interface ITransactionsState {
  /**
   * This tracks the active filters the user chooses via the filter next to the
   * search button on the UI, such as `disputes` and `undelivered`.
   */
  activeFilters: ITransactionFilter[] | null;
  /**
   * Tracks the active list view tab, whether `In Transit` or `All Transactions`.
   */
  activeListType: IActiveTxnListType | null;
  /**
   * Credit limit exceeded date
   */
  creditLimitExceededDate?: number;
  /**
   * Selects the `startDate` and `endDate` range from which the transactions should
   * be retrieved.
   */
  dateRange: IDateRangeType | null;
  /**
   * Tracks the state of the transaction sidebar in dealer-web.
   */
  isOpenTransactionSidebar: boolean;
  /**
   * The page number to to fetch from with pagination.
   */
  pageNo: number;
  /**
   * The search key entered by the user.
   */
  searchKey: string;
  /**
   * This tracks the active bulk purchase model selected by the user.
   */
  selectedBulkPurchaseModel?: IBulkPurchaseAccountStatement;
  /**
   * This tracks the selected payment details selected by the user.
   */
  selectedPaymentDetails: IPaymentAccountStatement | null;
  /**
   * This is a setter for credit limit exceeded date
   */
  onUpdateCreditLimitExceededDate: (date?: number) => void;
  /**
   * Tracks the state of the transaction sidebar in dealer-web.
   */
  onUpdateIsOpenTransactionSidebar: (isOpen: boolean) => void;
  /**
   * This replaces the `selectedPaymentDetails` with a new value.
   * @param paymentDetails The new payment details to replace the old ones.
   */
  onUpdateSelectedPaymentDetails: (
    paymentDetails: IPaymentAccountStatement | null
  ) => void;
  /**
   * The replaces the `activeFilters` with a new set of filters. When passed a
   * null value, it will clear all the filters.
   * @param filters The new filters to replace the old ones.
   */
  onUpdateTxnActiveFilters: (filters: ITransactionFilter[] | null) => void;
  /**
   * Updates the `activeListType` object to with a new value `type`
   * @param type The new list view to replace. Eg `In Transit` or `All Transactions`.
   */
  onUpdateTxnActiveListType: (type: IActiveTxnListType | null) => void;
  /**
   * Updates the transactions `dateRange` (start & end dates) from the calender picker.
   * @param dateRange The new date range to consider.
   */
  onUpdateTxnDateRange: (dateRange: IDateRangeType | null) => void;
  /**
   * Updates the active `pageNo` for the transactions pagination.
   * @param pageNo The new page number to consider.
   */
  onUpdateTxnPageNo: (pageNo: number) => void;
  /**
   * This replaces the transactions `searchKey` query for fetching results.
   * @param newSearch The new search key to consider.
   */
  onUpdateTxnSearchKey: (newSearch: string) => void;
  /**
   * This replaces the `selectedStatementType` with a new value.
   */
  onUpdateSelectedBulkPurchaseModel: (
    selectedBulkPurchaseModel?: IBulkPurchaseAccountStatement
  ) => void;
  clearTxnStore: () => void;
}

export type IPersistableTransactionState = Pick<
  ITransactionsState,
  | 'activeFilters'
  | 'activeListType'
  | 'creditLimitExceededDate'
  | 'dateRange'
  | 'pageNo'
  | 'searchKey'
  | 'selectedBulkPurchaseModel'
>;

export type TSetTransaction = StoreApi<ITransactionsState>['setState'];
